/* eslint-disable max-len */
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormLabel,
  CFormFeedback,
  CFormCheck,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { 
  cilLockLocked, 
  cilLockUnlocked, 
  cilUser,
  cilShieldAlt,
  cilCheckCircle,
  cilWarning,
  cilFingerprint,
  cilArrowRight,
} from '@coreui/icons'
import { connect } from 'react-redux'
import {
  accountDetailsRequestAction,
  loginActionRequest,
  verify2faRequestAction,
} from '../../store/account/userActions'
import { useDispatch } from 'react-redux'
import { useTranslation } from '../../hooks/useTranslation'
import './Login.scss'

const Login = ({ ...props }) => {
  const { loginRequest, user, verify2faRequest } = props
  const { t } = useTranslation()
  
  const [state, setState] = useState({
    data: {
      username: '',
      password: '',
    },
    errors: {},
  })

  const [showModal, setShowModal] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [passwordVisibility, setPasswordVisibility] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  const [focusedField, setFocusedField] = useState('')
  
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    if (user?.result?.two_factor_auth) {
      setShowModal(true)
    }
  }, [user?.result?.two_factor_auth])

  useEffect(() => {
    // Add animated gradient background
    const loginPage = document.querySelector('.login-page')
    if (loginPage) {
      loginPage.classList.add('animated-bg')
    }
    
    return () => {
      if (loginPage) {
        loginPage.classList.remove('animated-bg')
      }
    }
  }, [])

  // Load remembered username on first render
  useEffect(() => {
    const storedRemember = localStorage.getItem('rememberMe') === 'true'
    const storedUsername = localStorage.getItem('rememberedUsername') || ''
    if (storedRemember) {
      setRememberMe(true)
      setState(prev => ({
        ...prev,
        data: {
          ...prev.data,
          username: storedUsername,
        },
      }))
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const { data } = state
    const errors = validate()
    
    if (Object.keys(errors).length === 0) {
      setIsLoading(true)
      
      const payload = {
        data,
        navigate,
      }
      
      // Persist username if remember me is enabled
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true')
        localStorage.setItem('rememberedUsername', data.username || '')
      } else {
        localStorage.removeItem('rememberMe')
        localStorage.removeItem('rememberedUsername')
      }

      try {
        await loginRequest(payload)
        
        if (user?.two_factor_auth) {
          setShowModal(true)
        }
      } catch (error) {
        console.error('Login error:', error)
      } finally {
        setIsLoading(false)
      }
    } else {
      setState(prevState => ({
        ...prevState,
        errors,
      }))
      
      // Shake animation for errors
      const form = document.querySelector('.login-form-card')
      if (form) {
        form.classList.add('shake')
        setTimeout(() => form.classList.remove('shake'), 500)
      }
    }
  }

  const validate = () => {
    const { data } = state
    const errors = {}
    
    if (!data?.username) {
      errors.username = t('usernameRequired')
    }
    
    if (!data?.password) {
      errors.password = t('passwordRequired')
    } else if (data.password.length < 4) {
      errors.password = t('passwordTooShort')
    }

    return errors
  }

  const handleChange = (e) => {
    setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [e.target.id]: e.target.value,
      },
      errors: {
        ...prevState.errors,
        [e.target.id]: '',
      },
    }))
  }

  const handleVerify = async () => {
    try {
      setIsLoading(true)
      const userId = user.id
      const is_enabled = user.two_factor_auth
      const totp_code = inputValue.replace(/-/g, '')
      
      const payload = {
        userId: userId,
        totp_code: totp_code,
        is_enabled: is_enabled,
        navigate,
        loginPayload: {
          username: state.data.username,
          password: state.data.password,
        },
      }

      await verify2faRequest(payload)
      setInputValue('')
      setShowModal(false)
      
    } catch (error) {
      console.error('2FA verification error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    let value = e.target.value
    value = value.replace(/-/g, '')
    value = value.replace(/[^0-9]/g, '').slice(0, 6)
    if (value.length > 3) {
      value = value.slice(0, 3) + '-' + value.slice(3)
    }
    setInputValue(value)
  }

  const { data, errors } = state

  const handleModalEnterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify()
    }
  }

  return (
    <div className="login-page">
      {/* Animated Background Elements */}
      <div className="animated-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="floating-gradient"></div>
      </div>

      <CContainer fluid className="h-100 d-flex align-items-center justify-content-center p-0">
        <div className="login-container">
          <div className="login-wrapper">
            <CCard className="login-card border-0 shadow-lg overflow-hidden">
              <CRow className="g-0">
                  {/* Left Side - Login Form */}
                  <CCol md={6} className="login-form-section">
                    <CCardBody className="p-5">
                      <div className="login-header mb-5">
                        <div className="logo-wrapper mb-4">
                          <div className="logo-icon">
                            <CIcon icon={cilShieldAlt} size="3xl" />
                          </div>
                        </div>
                        <h2 className="login-title">{t('welcomeBack')}</h2>
                        <p className="login-subtitle text-medium-emphasis">
                          {t('signInToContinue')}
                        </p>
                      </div>

                      <CForm onSubmit={handleSubmit} className="login-form">
                        {/* Username Input */}
                        <div className={`form-floating-group mb-4 ${focusedField === 'username' ? 'focused' : ''}`}>
                          <CInputGroup className="modern-input-group">
                            <CInputGroupText className="input-icon">
                              <CIcon icon={cilUser} />
                            </CInputGroupText>
                            <CFormInput 
                              id="username"
                              type="text"
                              placeholder={t('username')} 
                              autoComplete="username"
                              value={data?.username}
                              onChange={handleChange}
                              onFocus={() => setFocusedField('username')}
                              onBlur={() => setFocusedField('')}
                              invalid={!!errors?.username}
                              className="modern-input"
                              disabled={isLoading}
                            />
                            <div className="input-focus-border"></div>
                          </CInputGroup>
                          {errors?.username && (
                            <CFormFeedback className="d-block mt-2 animated-error">
                              <CIcon icon={cilWarning} size="sm" className="me-1" />
                              {errors.username}
                            </CFormFeedback>
                          )}
                        </div>
                        
                        {/* Password Input */}
                        <div className={`form-floating-group mb-4 ${focusedField === 'password' ? 'focused' : ''}`}>
                          <CInputGroup className="modern-input-group">
                            <CInputGroupText className="input-icon">
                              <CIcon icon={cilShieldAlt} />
                            </CInputGroupText>
                            <CFormInput
                              id="password"
                              type={passwordVisibility ? 'text' : 'password'}
                              placeholder={t('password')}
                              autoComplete="current-password"
                              value={data?.password}
                              onChange={handleChange}
                              onFocus={() => setFocusedField('password')}
                              onBlur={() => setFocusedField('')}
                              invalid={!!errors?.password}
                              className="modern-input"
                              disabled={isLoading}
                            />
                            <CInputGroupText 
                              onClick={() => setPasswordVisibility(!passwordVisibility)}
                              className="password-toggle"
                            >
                              <CIcon icon={passwordVisibility ? cilLockUnlocked : cilLockLocked} />
                            </CInputGroupText>
                            <div className="input-focus-border"></div>
                          </CInputGroup>
                          {errors?.password && (
                            <CFormFeedback className="d-block mt-2 animated-error">
                              <CIcon icon={cilWarning} size="sm" className="me-1" />
                              {errors.password}
                            </CFormFeedback>
                          )}
                        </div>

                        {/* Remember Me */}
                        <CRow className="mb-4">
                          <CCol xs={6}>
                            <CFormCheck 
                              id="rememberMe"
                              label={t('rememberMe')}
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              className="modern-checkbox"
                            />
                          </CCol>
                        </CRow>
                        
                        {/* Login Button */}
                        <div className="d-grid gap-2">
                          <CButton 
                            type="submit" 
                            className="login-button text-white"
                            disabled={isLoading}
                            size="lg"
                          >
                            {isLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                {t('signingIn')}
                              </>
                            ) : (
                              <>
                                {t('signIn')}
                                <CIcon icon={cilArrowRight} className="ms-2" />
                              </>
                            )}
                          </CButton>
                        </div>

                        {/* Removed social login */}
                      </CForm>
                    </CCardBody>
                  </CCol>

                  {/* Right Side - Welcome Section */}
                  <CCol md={6} className="welcome-section d-none d-md-block">
                    <div className="welcome-content">
                      <div className="welcome-overlay"></div>
                      <div className="welcome-text">
                        <h1 className="welcome-title">
                          {t('startYourJourney')}
                        </h1>
                        <p className="welcome-description">
                          {t('loginDescription')}
                        </p>
                        <div className="feature-list">
                          <div className="feature-item">
                            <CIcon icon={cilCheckCircle} className="feature-icon" />
                            <span>{t('secureAuthentication')}</span>
                          </div>
                          <div className="feature-item">
                            <CIcon icon={cilCheckCircle} className="feature-icon" />
                            <span>{t('dataProtection')}</span>
                          </div>
                          <div className="feature-item">
                            <CIcon icon={cilCheckCircle} className="feature-icon" />
                            <span>{t('24_7Support')}</span>
                          </div>
                        </div>
                        <div className="decoration-circles">
                          <div className="circle circle-1"></div>
                          <div className="circle circle-2"></div>
                          <div className="circle circle-3"></div>
                        </div>
                      </div>
                    </div>
                  </CCol>
                </CRow>
              </CCard>
            </div>
          </div>
        </CContainer>

      {/* Enhanced 2FA Modal */}
      <CModal 
        visible={showModal} 
        onClose={() => setShowModal(false)}
        alignment="center"
        className="two-factor-modal"
        backdrop="static"
      >
        <CModalHeader className="two-factor-header">
          <CModalTitle className="d-flex align-items-center">
            <CIcon icon={cilFingerprint} size="xl" className="me-3" />
            {t('twoFactorAuthentication')}
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="two-factor-body">
          <div className="text-center mb-4">
            <div className="security-icon-wrapper">
              <CIcon icon={cilShieldAlt} size="3xl" className="security-icon" />
            </div>
            <p className="security-message mt-3">
              {t('enter2FACodeMessage')}
            </p>
          </div>
          
          <CFormLabel className="fw-semibold mb-3">
            {t('verificationCode')}
          </CFormLabel>
          <CFormInput
            type="text"
            placeholder="XXX-XXX"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleModalEnterKeyPress}
            className="two-factor-input mb-3"
            autoFocus
            disabled={isLoading}
          />
          
          <div className="resend-code-wrapper text-center mt-3">
            <small className="text-muted">
              {t('didntReceiveCode')} 
              <a href="#" className="resend-link ms-1">{t('resend')}</a>
            </small>
          </div>
        </CModalBody>
        <CModalFooter className="two-factor-footer">
          <CButton 
            color="secondary" 
            onClick={() => setShowModal(false)}
            disabled={isLoading}
            className="cancel-btn"
          >
            {t('cancel')}
          </CButton>
          <CButton 
            color="primary" 
            onClick={handleVerify}
            disabled={isLoading || inputValue.length < 6}
            className="verify-btn"
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {t('verifying')}
              </>
            ) : (
              <>
                <CIcon icon={cilCheckCircle} className="me-2" />
                {t('verify')}
              </>
            )}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    user: state.userReducer.user || { two_factor_auth: false },
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    loginRequest: (creds) => {
      dispatch(loginActionRequest(creds))
    },
    accountDetailsRequest: () => {
      dispatch(accountDetailsRequestAction())
    },
    verify2faRequest: (payload) => {
      dispatch(verify2faRequestAction(payload))
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)